import { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type {
  EquipmentInstanceSurvey,
  Exercise,
  LocationUnavailableEntry,
} from '@jackedlogs/shared';

import { Button, Dialog, ToggleRow } from '../index';

import { machineLabel } from './machineLabel';
import { useExerciseName } from './exerciseName';
import { locationLabel } from './locationLabel';
import type { useTheme } from './theme';
import type { UnavailableAnswer } from './_useLocationUnavailable';

// EQ-1-GYM-COLDSTART R2 — the question the ✕ asks.
//
// ⭐ Its shape is Antenore's, 2026-08-17: *"La X apre il popup e chiede se
// vuoi rimuovere e mostra tutte le istanze di quell'attrezzo da preselezionare
// (checkbox) … quindi quale istanza vuoi togliere?"*. The athlete does not
// declare "the leg press is not here": they declare WHICH leg press. The
// granularity is picked per answer, not decided once by us.
//
// ⛔ NOT the `confirm` seam. `ConfirmFn` does title/body/yes/no and hands back
// a boolean — a list of checkboxes cannot travel through it (seams.ts). So
// this is the in-app <Dialog> primitive, pure RN, identical on mobile and
// web-rnw, with no platform dialog anywhere in the path.
//
// ⭐ The Default row is ALWAYS offered, and that is the difference between the
// feature working and the feature being empty. Measured 2026-08-17:
// equipment-instance tags in production are ZERO across all three athletes, so
// today every exercise has exactly one instance — that one. Rendering it only
// when the survey happens to carry an `unnamed` bucket would also lose the
// exercise being logged for the first time, which has no saved history at all.
// Same rule <MachineTray> already follows for the same reason.
type Props = {
  visible: boolean;
  /** The exercise the ✕ was pressed on. Its NAME is resolved in here, not by
   *  the caller: the picker itself must not depend on the locale hook just to
   *  mount a dialog it may never open. */
  exercise: Exercise;
  /** The place slug the declaration is keyed by. */
  place: string;
  /** Every instance the history window knows about, default excluded. */
  survey: EquipmentInstanceSurvey;
  /** What is already declared for this pair — the pre-tick. */
  declared: LocationUnavailableEntry | undefined;
  onCancel: () => void;
  onConfirm: (answer: UnavailableAnswer) => void;
  theme: ReturnType<typeof useTheme>;
};

export function UnavailableHereDialog({
  visible,
  exercise,
  place,
  survey,
  declared,
  onCancel,
  onConfirm,
  theme,
}: Props) {
  const { t } = useTranslation();
  const getExerciseName = useExerciseName();
  const styles = makeStyles(theme);
  const exerciseName = getExerciseName(exercise.name);

  const [instances, setInstances] = useState<readonly string[]>([]);
  const [unnamed, setUnnamed] = useState(false);

  // Seeded on OPEN, from what is already declared: re-opening the dialog has to
  // show the answer the athlete gave, otherwise correcting it is impossible —
  // `declareUnavailable` REPLACES rather than merges (by design, so a tick can
  // be taken back), and a dialog that always opened blank would silently wipe
  // the other instances on every confirm.
  useEffect(() => {
    if (!visible) return;
    setInstances(declared?.instances ?? []);
    setUnnamed(declared?.unnamed === true);
  }, [visible, declared]);

  const toggleInstance = (value: string, next: boolean): void =>
    setInstances((prev) =>
      next
        ? prev.includes(value)
          ? prev
          : [...prev, value]
        : prev.filter((v) => v !== value),
    );

  const anyTicked = unnamed || instances.length > 0;
  // ⛔ Nothing is ticked by default and the confirm stays off until something
  // is (Antenore, explicitly: a mis-tap must cost nothing).
  //
  // ⚠️ The one exception, and it is not a loosening: when a declaration ALREADY
  // exists, an empty answer means "none of them is missing after all", which is
  // a real answer and the only way back for an exercise that did NOT sink (one
  // machine of two declared missing leaves no ✚ anywhere, because nothing sank).
  // `declareUnavailable` already anticipates it — an answer that ticks nothing
  // removes the entry rather than storing an empty one.
  const canConfirm = anyTicked || declared !== undefined;

  return (
    <Dialog
      theme={theme}
      visible={visible}
      onRequestClose={onCancel}
      // An explicit Cancel: a stray tap must not be read as an answer either
      // way, and this dialog's whole reason to exist is that nothing here may
      // be inferred.
      dismissOnBackdropPress={false}
      title={t('swap.unavailableTitle', { defaultValue: 'Not here?' })}
    >
      <Text style={styles.body}>
        {/* ⚠️ Interpolation through i18next placeholders, NOT a template
            literal: the CN-I18N-DEFAULTVALUE-DRIFT gate compares this
            `defaultValue` byte-for-byte with en.json, and a `${}` in the
            source could never match what ships. */}
        {t('swap.unavailableBody', {
          exercise: exerciseName,
          place: locationLabel(place),
          defaultValue:
            'Tick the {{exercise}} gear that is not at {{place}}. Only what you tick is recorded.',
        })}
      </Text>
      <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
        {survey.named.map((known) => (
          <ToggleRow
            key={known.value}
            theme={theme}
            label={machineLabel(known.value, t)}
            value={instances.includes(known.value)}
            onValueChange={(next) => toggleInstance(known.value, next)}
          />
        ))}
        <ToggleRow
          theme={theme}
          label={t('swap.defaultMachine', { defaultValue: 'Default machine' })}
          value={unnamed}
          onValueChange={setUnnamed}
        />
      </ScrollView>
      {/* The reason the Save is off, WRITTEN. It used to live only in the
          accessibilityHint below, which renders nothing at all — so the
          explanation reached a screen reader and missed the sighted reader
          it was written for. Same string, both audiences. */}
      {!canConfirm && (
        <Text style={styles.confirmHint}>
          {t('swap.unavailableConfirmHint', {
            defaultValue: 'Tick at least one to save.',
          })}
        </Text>
      )}
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="ghost"
          label={t('mobile.logger.cancel', { defaultValue: 'Cancel' })}
          onPress={onCancel}
        />
        <Button
          theme={theme}
          variant="primary"
          disabled={!canConfirm}
          // The reason it is off, spoken. The greyed box next to an untouched
          // list says it to anyone who can see the list; without this a
          // screen-reader user got only "Save, dimmed".
          {...(canConfirm
            ? {}
            : {
                accessibilityHint: t('swap.unavailableConfirmHint', {
                  defaultValue: 'Tick at least one to save.',
                }),
              })}
          label={t('swap.unavailableConfirm', { defaultValue: 'Save' })}
          onPress={() => onConfirm({ instances: [...instances], unnamed })}
        />
      </View>
    </Dialog>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    // ⛔ `base`, not `sm`. This is the dialog's primary instruction, not a
    // footnote — <UnsavedChangesDialog> sets the same slot at `base`, and the
    // sm/ink3 recipe next door (`consequence`) is deliberately a footnote.
    // Reduced-vision readability is a floor here (a11y audit, 2026-08-17).
    body: { color: p.ink2, fontSize: t.sizes.base },
    // Capped so a long list of machines scrolls inside the card instead of
    // pushing the action row past the <Dialog>'s own maxHeight.
    list: { maxHeight: 260 },
    // `sm`, not `xs`: it is the only thing explaining a dead button, so it has
    // to be read at a glance, not squinted at. Still below `body` because it
    // answers a question the body already framed.
    confirmHint: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      marginTop: s.s3,
      textAlign: 'right',
    },
    actions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: s.s3,
    },
  });
}
